import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, useId } from 'vue';
import { Chart as ChartJS, ChartOptions, registerables } from 'chart.js/auto';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { i18n } from '@/i18n';
import { Line } from 'vue-chartjs';
import {
  MetricType,
  metricTypesToSummaryAmountsKeyMapper,
  SummaryAmountsByDimension,
} from '@/inventoryDashboardModule/domains/entities/Dashboard';
import { IDate, IYearMonth } from '@/coreDomainsModule/date';


export default /*@__PURE__*/_defineComponent({
  __name: 'AmountChart',
  props: {
    label: {},
    forecastLabel: {},
    metricType: {},
    amounts: {},
    fromYearMonth: {},
    toYearMonth: {}
  },
  setup(__props: any) {

const t = i18n.t;

const props = __props;

const chartId = useId();
ChartJS.register(...registerables);

const periodLength = computed(() => DateService.calculateIYearMonthsDiff(props.fromYearMonth, props.toYearMonth, true));

const startMonthDiff = computed(() => {
  return DateService.calculateIYearMonthsDiff(props.fromYearMonth, DateService.getShiftedIYearMonthFromNowDateByMonths(0));
});

const amountData = computed(() => {
  const keyFromMetricType = metricTypesToSummaryAmountsKeyMapper[props.metricType];
  const res: { x: number; y: number }[] = [];
  for (let i = 0; i < periodLength.value + 1; i++) {
    const t: IDate = {
      ...DateService.getShiftedIYearMonthFromNowDateByMonths(startMonthDiff.value + i),
      day: 1,
    };
    const targetAmountData = props.amounts.find((a) => DateService.compareYearMonths(a.yearMonth, t) === 0);
    if (!targetAmountData || !targetAmountData[keyFromMetricType]) continue;
    res.push({ x: i, y: targetAmountData[keyFromMetricType] });
  }
  return res;
});

const chartData = computed(() => ({
  datasets: [
    {
      label: props.label,
      data: amountData.value,
      borderColor: '#1e92ef',
      backgroundColor: '#1e92ef',
      segment: {
        // eslint-disable-next-line jsdoc/require-jsdoc
        borderDash: (ctx) => {
          const x = ctx.p0.parsed.x;
          if (x >= Math.abs(startMonthDiff.value) - 1) {
            return [4, 4];
          }
          return undefined;
        },
      },
    },
  ],
}));

const chartOptions = computed<ChartOptions<'line'>>(() => ({
  responsive: true,
  maintainAspectRatio: false,
  parsing: false,
  scales: {
    x: {
      type: 'linear',
      min: 0,
      max: periodLength.value,
      ticks: {
        stepSize: 1,
        // eslint-disable-next-line jsdoc/require-jsdoc
        callback: (value) => {
          const v = Number(value);
          if (!Number.isInteger(v)) return '';
          return DateService.convertIYearMonthToYYYYMMWithSlash(
            DateService.getShiftedIYearMonthFromNowDateByMonths(startMonthDiff.value + v)
          );
        },
      },
    },
  },
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false,
  },
  hover: {
    mode: 'nearest',
    axis: 'x',
    intersect: false,
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
      callbacks: {
        /**
         * ツールチップの数値を小数1桁目まで確実に表示する
         * @param ctx - Tooltipのobj
         */
        title: (ctx) =>
          DateService.convertIYearMonthToYYYYMMWithSlash(
            DateService.getShiftedIYearMonthFromNowDateByMonths(startMonthDiff.value + ctx[0].parsed.x)
          ),
        /**
         * ツールチップの数値を小数1桁目まで確実に表示する
         * @param ctx - Tooltipのobj
         */
        label: (ctx) => {
          const v = ctx.parsed.y;
          if (v == null) return '';
          const label = props.forecastLabel
            ? ctx.parsed.x >= Math.abs(startMonthDiff.value)
              ? props.forecastLabel
              : ctx.dataset.label
            : ctx.dataset.label;
          return `${label}: ${v.addComma()}`;
        },
      },
    },
  },
}));

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Line), {
    id: _unref(chartId),
    data: chartData.value,
    options: chartOptions.value
  }, null, 8, ["id", "data", "options"]))
}
}

})